import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { purchaseService, PRODUCT_IDS, ENTITLEMENT_ID } from "@/lib/purchaseService";
import { storage } from "@/lib/storage";

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

// Free tier limits
const FREE_LIMITS = {
  aiMessages: 0,    // No AI messages in free mode
  aiCards: 0,       // No AI cards in free mode
  aiGifts: 0,       // No AI gifts in free mode
  contacts: 9999,   // Unlimited manual contacts (but no import)
};

// Features that require premium
export const PREMIUM_FEATURES = {
  AI_MESSAGES: 'aiMessages',
  AI_CARDS: 'aiCards',
  AI_GIFTS: 'aiGifts',
  EXPORT_CONTACTS: 'exportContacts',
  PUSH_NOTIFICATIONS: 'pushNotifications',
  SCHEDULED_MESSAGES: 'scheduledMessages',
  WHATSAPP_SEND: 'whatsappSend',
  SMS_SEND: 'smsSend',
  CUSTOM_EVENT_TYPES: 'customEventTypes',
  FULL_HISTORY: 'fullHistory',
};

export const SubscriptionProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasSeenSubscriptionScreen, setHasSeenSubscriptionScreen] = useState(false);
  const [offerings, setOfferings] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [usage, setUsage] = useState({
    aiMessages: 0,
    aiCards: 0,
    aiGifts: 0,
    contacts: 0
  });

  // Check if user is premium or admin
  const checkUserStatus = useCallback(async () => {
    try {
      const userStr = await storage.getItem("skybday_user");
      if (userStr) {
        const user = JSON.parse(userStr);
        // Check if admin
        if (user.is_admin || user.role === "admin" || user.email === "joelott86@gmail.com") {
          setIsAdmin(true);
          setIsPremium(true);
          return true;
        }
        // Check if premium (set by backend/admin)
        if (user.is_premium) {
          setIsPremium(true);
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }, []);

  // Check if user has seen subscription screen
  const checkSubscriptionScreenStatus = useCallback(async () => {
    try {
      const hasSeen = await storage.getItem("skybday_seen_subscription");
      setHasSeenSubscriptionScreen(hasSeen === "true");
      return hasSeen === "true";
    } catch (e) {
      return false;
    }
  }, []);

  // Mark subscription screen as seen
  const markSubscriptionScreenSeen = async () => {
    await storage.setItem("skybday_seen_subscription", "true");
    setHasSeenSubscriptionScreen(true);
  };

  // Initialize subscription status
  useEffect(() => {
    const initSubscription = async () => {
      setIsLoading(true);
      
      try {
        // First check if admin
        const isAdminUser = await checkUserStatus();
        if (isAdminUser) {
          setIsInitialized(true);
          setIsLoading(false);
          return;
        }

        // Initialize purchase service
        const success = await purchaseService.initialize();
        
        if (success) {
          // Get offerings
          const offs = purchaseService.getOfferings();
          setOfferings(offs);
          
          // Check premium status
          const premiumActive = purchaseService.isPremiumActive;
          setIsPremium(premiumActive);
          
          // Save status
          localStorage.setItem("skybday_premium_status", premiumActive ? "true" : "false");
          
          // Get customer info
          const info = purchaseService.getCustomerInfo();
          setCustomerInfo(info);
        }

        // Load usage from storage
        const savedUsage = localStorage.getItem("skybday_usage");
        if (savedUsage) {
          setUsage(JSON.parse(savedUsage));
        }

        // Check if seen subscription screen
        await checkSubscriptionScreenStatus();

        setIsInitialized(true);
      } catch (error) {
        console.error("Subscription init error:", error);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initSubscription();
    
    // Listen for subscription purchase completion to sync with backend
    const handlePurchaseComplete = async (event) => {
      const { premium, transactionId, productId } = event.detail;
      
      if (premium && transactionId) {
        console.log("Syncing subscription with backend:", transactionId);
        try {
          const token = await storage.getItem("skybday_token");
          if (token) {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/sync`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({
                transaction_id: transactionId,
                product_id: productId,
                is_active: true
              })
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log("Subscription synced with backend:", data);
            }
          }
        } catch (error) {
          console.error("Error syncing subscription:", error);
        }
      }
      
      // Update local state
      setIsPremium(premium);
      setHasSeenSubscriptionScreen(true);
    };
    
    window.addEventListener("skybday_purchase_complete", handlePurchaseComplete);
    
    return () => {
      window.removeEventListener("skybday_purchase_complete", handlePurchaseComplete);
    };
  }, [checkUserStatus, checkSubscriptionScreenStatus]);

  // Redirect to subscription screen if needed
  // This should ONLY redirect ONCE - the first time after signup
  useEffect(() => {
    const checkAndRedirect = async () => {
      // Don't redirect if not initialized yet
      if (!isInitialized || isLoading) return;
      
      // Don't redirect if already on auth, pricing, or public pages
      const excludedPaths = ['/auth', '/pricing', '/verify-email', '/reset-password', '/legal', '/privacy', '/terms', '/onboarding'];
      if (excludedPaths.some(path => location.pathname.startsWith(path))) return;
      
      // Don't redirect if admin
      if (isAdmin) return;
      
      // Don't redirect if already premium
      if (isPremium) {
        // IMPORTANT: Mark subscription screen as seen for premium users
        // This prevents redirect loop after successful purchase
        const hasSeen = await storage.getItem("skybday_seen_subscription");
        if (hasSeen !== "true") {
          await storage.setItem("skybday_seen_subscription", "true");
          setHasSeenSubscriptionScreen(true);
        }
        return;
      }
      
      // Check if user is logged in
      const token = await storage.getItem("skybday_token");
      if (!token) return;
      
      // Check if user has ALREADY seen subscription screen (persistent flag)
      const hasSeen = await storage.getItem("skybday_seen_subscription");
      
      // CRITICAL FIX: Only redirect if user has NEVER seen the subscription screen
      // Once they've seen it (clicked "Continuer sans essai" or completed purchase), 
      // they should NEVER be redirected again
      if (hasSeen !== "true") {
        // Check if we already redirected this app session (prevent infinite loop)
        const alreadyRedirected = await storage.getItem("skybday_pricing_redirect_done");
        if (alreadyRedirected !== "true") {
          console.log("First time user - redirecting to subscription screen (once only)");
          await storage.setItem("skybday_pricing_redirect_done", "true");
          navigate("/pricing?required=true");
        }
      }
    };

    checkAndRedirect();
  }, [isInitialized, isLoading, isPremium, isAdmin, location.pathname, navigate]);

  // Check if user can use a premium feature
  const canUseFeature = (feature) => {
    // Admins can use everything
    if (isAdmin) return true;
    
    // Premium users can use everything
    if (isPremium) return true;
    
    // Free users can only use basic features
    // These are the features available in minimal mode
    const freeFeatures = [
      'viewContacts',
      'createContact',
      'editContact',
      'deleteContact',
      'viewCalendar',
      'viewBirthdays',
      'mergeDuplicates'
    ];
    
    return freeFeatures.includes(feature);
  };

  // Check if user can use feature and show upgrade prompt if not
  const requirePremium = (feature, showPrompt = true) => {
    if (canUseFeature(feature)) {
      return true;
    }
    
    if (showPrompt) {
      // Redirect to pricing with feature context
      navigate(`/pricing?feature=${feature}&required=false`);
    }
    
    return false;
  };

  // Use a feature (increment usage counter)
  const useFeature = async (type) => {
    if (isAdmin || isPremium) return true;
    
    // Free users can't use premium features
    return false;
  };

  // Purchase subscription
  const purchasePackage = async (packageId = PRODUCT_IDS.MONTHLY) => {
    try {
      setIsLoading(true);
      const result = await purchaseService.purchaseMonthlySubscription();
      
      if (result.success) {
        setIsPremium(true);
        localStorage.setItem("skybday_premium_status", "true");
        await markSubscriptionScreenSeen();
        return { success: true };
      }
      
      return { success: false, error: result.error };
    } catch (error) {
      console.error("Purchase error:", error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Restore purchases
  const restorePurchases = async () => {
    try {
      setIsLoading(true);
      const result = await purchaseService.restorePurchases();
      
      if (result.success && result.isPremium) {
        setIsPremium(true);
        localStorage.setItem("skybday_premium_status", "true");
        await markSubscriptionScreenSeen();
        return { success: true, restored: true };
      }
      
      return { success: true, restored: false };
    } catch (error) {
      console.error("Restore error:", error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Login user (for RevenueCat identification)
  const loginUser = async (userId) => {
    try {
      // Re-check admin status
      await checkUserStatus();
      
      // Re-initialize purchase service
      await purchaseService.initialize();
      const premiumActive = purchaseService.isPremiumActive;
      setIsPremium(premiumActive);
      
      if (premiumActive) {
        const info = purchaseService.getCustomerInfo();
        setCustomerInfo(info);
      }
    } catch (error) {
      console.error("RC login error:", error);
    }
  };

  // Logout user - reset premium status
  const logoutUser = async () => {
    setIsPremium(false);
    setIsAdmin(false);
    setCustomerInfo(null);
    setHasSeenSubscriptionScreen(false);
    setUsage({
      aiMessages: 0,
      aiCards: 0,
      aiGifts: 0,
      contacts: 0
    });
    // Clear storage
    localStorage.removeItem("skybday_premium_status");
    localStorage.removeItem("skybday_usage");
    await storage.removeItem("skybday_seen_subscription");
    console.log("User logged out - premium status reset");
  };

  // Continue without subscription (minimal mode)
  const continueWithoutSubscription = async () => {
    await markSubscriptionScreenSeen();
    // Clear redirect flag to prevent any future issues
    await storage.removeItem("skybday_pricing_redirect_done");
    navigate("/dashboard");
  };

  return (
    <SubscriptionContext.Provider
      value={{
        isInitialized,
        isLoading,
        isPremium,
        isAdmin,
        hasSeenSubscriptionScreen,
        offerings,
        customerInfo,
        usage,
        canUseFeature,
        requirePremium,
        useFeature,
        purchasePackage,
        restorePurchases,
        loginUser,
        logoutUser,
        continueWithoutSubscription,
        markSubscriptionScreenSeen,
        FREE_LIMITS,
        PREMIUM_FEATURES,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
